import { Avatar, Box, Dialog, Divider, IconButton, Link, List, ListItem, Typography } from '@mui/material'
import type React from 'react'
import { MButton } from 'components'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'
import CloseIcon from '@mui/icons-material/Close'

interface PrivacyDialogProps {
    open: boolean
    handleClick: () => void
}

export const PrivacyDialog: React.FC<PrivacyDialogProps> = ({ open, handleClick }) => {
    const isMobile = useMobileBreakpoints()
    const handleLogin = (): void => {
        handleClick()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClick}
            PaperProps={{
                sx: {
                    width: isMobile ? '90%' : '80%',
                    maxWidth: '100%',
                    m: 2
                }
            }}
        >
            <IconButton
                onClick={handleClick}
                sx={{ position: 'absolute', right: 8, top: 8 }}>
                <CloseIcon />
            </IconButton>
            <Box px={isMobile ? 5 : 5} py={7} maxWidth='100%'>
                <Box display='flex' flexDirection='column' alignItems='center'>
                    <Avatar sx={{ background: '#0092bd', p: 3 }}>
                        <PrivacyTipIcon sx={{ fontSize: 50, color: '#fff' }} />
                    </Avatar>
                </Box>
                <Box mt={2}>
                    <Typography textTransform="capitalize" textAlign="center" fontSize={isMobile ? 18 : 24} fontWeight={600} mb={5}>AlexRenew Privacy Policy & Utility Notification Enrollment Statement</Typography>
                    <Typography textAlign="center" fontSize={16} fontWeight={400} mb={2}></Typography>
                    <Typography fontSize={16} fontWeight={400}>AlexRenew is committed to protecting the privacy and security of customer information. This policy explains how we collect, use, store, and safeguard personal data, including mobile numbers used for SMS, email, and automated voice notifications.</Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>1. How Personal Data Is Collected and Used</Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>We collect personal information directly from customers when they establish utility service, create an online account, update contact information, make a payment, or interact with the AlexRenew customer portal. Information collected may include:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Name</ListItem>
                            <ListItem>Service address</ListItem>
                            <ListItem>Email address</ListItem>
                            <ListItem>Mobile phone number</ListItem>
                            <ListItem>Account number</ListItem>
                            <ListItem>Communication preferences</ListItem>
                            <ListItem>Billing, usage, and payment information</ListItem>
                        </List>
                        We use this information to:
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Provide water and wastewater services</ListItem>
                            <ListItem>Send billing reminders, payment confirmations, outage alerts, and important service notifications</ListItem>
                            <ListItem>Maintain and secure customer accounts</ListItem>
                            <ListItem>Verify customer identity</ListItem>
                            <ListItem>Support customer service operations</ListItem>
                            <ListItem>Improve our systems and communications</ListItem>
                            <ListItem>Comply with legal and regulatory requirements</ListItem>
                        </List>

                        AlexRenew <b>does not sell customer information</b> for marketing or commercial purposes.</Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>2. Utility Notification Enrollment (SMS, Email, IVR)</Typography>
                    <Typography fontSize={16} fontWeight={600} mb={2}>Automatic Enrollment<br /></Typography>
                    <Typography fontSize={16} fontWeight={400}>Customers are automatically enrolled to receive important account and service notifications by text message when establishing service with AlexRenew. These may include:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Billing reminders</ListItem>
                            <ListItem>Payment confirmations</ListItem>
                            <ListItem>Outage or emergency alerts</ListItem>
                            <ListItem>Service updates or account-related notices</ListItem>
                        </List>
                    </Typography>
                    <Typography fontSize={16} fontWeight={400}>Message and data rates may apply.<br /></Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>Message frequency varies based on account activity..<br /></Typography>
                    <Typography fontSize={16} fontWeight={600} mb={2}>Opt-Out Options<br /></Typography>
                    <Typography fontSize={16} fontWeight={400}>Customers may opt out of SMS notifications at any time by:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Replying <b>STOP</b> to any AlexRenew text message, or</ListItem>
                            <ListItem>Contacting AlexRenew Customer Service</ListItem>
                        </List>
                    </Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>Reply <b>HELP</b> for additional support.<br /></Typography>
                    <Typography fontSize={16} fontWeight={600} mb={2}>Customer Consent<br /></Typography>
                    <Typography fontSize={16} fontWeight={400}>By initiating utility service, customers <b>consent to receive SMS messages</b> related to their account as described in this policy.<br /></Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>AlexRenew does <b>not</b> share, sell, rent, or use customer mobile information for marketing or promotional purposes. Mobile numbers are used <b>only</b> to provide utility-related information and account services.<br /></Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>3. Disclosure of Personal Data to Third Parties</Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>AlexRenew may share limited customer information with trusted third-party service providers only when necessary to deliver utility services or support AlexRenew operations, including:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Billing and payment processors</ListItem>
                            <ListItem>SMS, email, and IVR messaging platforms</ListItem>
                            <ListItem>Hosting, security, and IT service providers</ListItem>
                            <ListItem>Regulatory agencies when required by law</ListItem>
                        </List>
                        All third-party vendors are contractually required to:
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Protect the data they receive</ListItem>
                            <ListItem>Use it solely to support AlexRenew services</ListItem>
                            <ListItem>Maintain compliance with applicable privacy and security standards</ListItem>
                        </List>
                        AlexRenew does <b>not</b> authorize third parties to use customer data for advertising or unrelated purposes.
                    </Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>4. How Data Is Stored and Protected</Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>AlexRenew uses administrative, technical, and physical safeguards to protect customer information, including:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Encrypted transmission of data</ListItem>
                            <ListItem>Secure, encrypted storage of personal information</ListItem>
                            <ListItem>Access controls and authentication requirements</ListItem>
                            <ListItem>Monitoring, auditing, and vulnerability testing</ListItem>
                            <ListItem>Regular system reviews and vendor security assessments</ListItem>
                        </List>
                        Customer data is retained only as long as necessary to deliver services, maintain regulatory records, or meet legal obligations.
                    </Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>5. How Mobile Phone Numbers Are Handled</Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>Mobile phone numbers are collected exclusively to deliver AlexRenew service-related notifications. Mobile numbers are:<br />
                        <List sx={{
                            listStyleType: 'disc',
                            pl: 3,
                            '& .MuiListItem-root': {
                                display: 'list-item',
                                paddingLeft: 0.5
                            }
                        }}>
                            <ListItem>Used only for essential account and service communications</ListItem>
                            <ListItem>Shared only with contracted messaging and communication processors</ListItem>
                            <ListItem>Protected using industry-standard encryption and security measures</ListItem>
                            <ListItem>Never sold, rented, shared, or used for marketing purposes</ListItem>
                            <ListItem>Able to be updated or removed at any time through the customer portal or Customer Service</ListItem>
                        </List>
                        Customers may opt out of SMS notifications at any time by replying <b>STOP</b>.
                    </Typography>
                    <Divider sx={{ py: 1 }} />
                    <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>6. Contact Information</Typography>
                    <Typography fontSize={16} fontWeight={400} mb={2}>For questions about this Privacy Policy or how your information is handled, please contact:<br />
                        <b>AlexRenew Customer Service</b><br />
                        Phone: 703-721-3500<br />
                        Email: support@alexrenew.com<br />
                        Website: <Link target='_blank' rel='noopener noreferrer' href='https://alexrenew.com/'>www.alexrenew.com</Link>
                    </Typography>
                </Box>
                <Box mt={3} textAlign="center">
                    <MButton
                        variant="contained"
                        size="large"
                        rounded="true"
                        texttransform="none"
                        sx={{ width: '200px' }}
                        onClick={handleLogin}
                    >
                        Go Back
                    </MButton>
                </Box>
            </Box>
        </Dialog>
    )
}
